import { TooltipModule } from '@openng/optimus-ui/tooltip';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  OnDestroy,
  output,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

import { TableModule } from '@openng/optimus-ui/table';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { CurrencyFormatPipe, BreakpointService, BtnLoaderDirective, SpinnerService } from '@zayan-erp/shared';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  DsmProductOption,
  DsmSelectedProductEntry,
  DsmSelectedProductValues
} from '../../../models/dsm-deal-options.models';
import { DsmDealOptionProduct } from '../../../models/dsm-deal-options-view.models';
import { toDsmAddendumProductPayload } from '../../../utils/dsm-addendum-product-payload.util';
import { DsmSelectedProductPanelComponent } from '../dsm-selected-product-panel/dsm-selected-product-panel.component';
import { DsmSelectedProductConfigFormComponent } from '../dsm-selected-product-config-form/dsm-selected-product-config-form.component';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { MessageModule } from '@openng/optimus-ui/message';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { DsmAddendumService } from '../../../services/dsm-addendum.service';
import { DsmPackageService } from '../../../services/dsm-package.service';
import { TagModule } from '@openng/optimus-ui/tag';
import { SelectModule } from '@openng/optimus-ui/select';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { DsmAddOptionPackagesTabComponent } from '../dsm-add-option-packages-tab/dsm-add-option-packages-tab.component';
import { DsmAddOptionScenariosTabComponent } from '../dsm-add-option-scenarios-tab/dsm-add-option-scenarios-tab.component';
import { DsmAddOptionOptionsPanelComponent } from '../dsm-add-option-options-panel/dsm-add-option-options-panel.component';
import { DsmSelectedProductMultiLocationFormComponent } from '../dsm-select-multiple-location-config-form/dsm-selected-product-multi-location-form.component';
import { DsmKitOptionConfigFormComponent } from '../dsm-kit-option-config-form/dsm-kit-option-config-form.component';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';

@Component({
  standalone: true,
  selector: 'dsm-add-option-modal',
  styleUrl: './dsm-add-option-modal.component.scss',
  imports: [
    CommonModule,
    TooltipModule,
    ReactiveFormsModule,
    TableModule,
    CheckboxModule,
    CurrencyFormatPipe,
    DsmSelectedProductPanelComponent,
    DialogModule,
    FloatLabelModule,
    TooltipModule,
    FormsModule,
    ButtonModule,
    MessageModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    SelectModule,
    SelectButtonModule,
    DsmAddOptionPackagesTabComponent,
    DsmAddOptionScenariosTabComponent,
    DsmAddOptionOptionsPanelComponent,
    DsmSelectedProductMultiLocationFormComponent,
    DsmSelectedProductConfigFormComponent,
    DsmKitOptionConfigFormComponent,
    BtnLoaderDirective
  ],
  templateUrl: './dsm-add-option-modal.component.html'
})
export class DsmAddOptionModalComponent implements OnDestroy {
  private readonly addendumService = inject(DsmAddendumService);
  private readonly packageService = inject(DsmPackageService);
  readonly #spinner = inject(SpinnerService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #isDesktop = toSignal(inject(BreakpointService).isDesktop$, { initialValue: true });

  modelId = input.required<string>();
  elevationId = input.required<string>();
  addendumId = input.required<string>();
  lotId = input.required<string>();
  addedProductIds = input.required<string[]>();
  addedProducts = input<DsmDealOptionProduct[]>([]);
  basketEntries = input<DsmSelectedProductEntry[]>([]);
  filtersVisible = model<boolean>(true);
  source = input<'addendum' | 'package' | 'basket'>('addendum');

  readonly isPackageSource = computed(() => this.source() === 'package');
  readonly isBasketSource = computed(() => this.source() === 'basket');
  readonly showPackagesTab = computed(() => !this.isPackageSource());
  readonly showScenariosTab = computed(() => !this.isPackageSource());
  readonly panelAddendumId = computed(() => (this.isPackageSource() ? null : this.addendumId()));
  readonly panelLotId = computed(() => (this.isPackageSource() ? null : this.lotId() || null));

  dialogClose = output<void>();
  /** Room Replacement Rules: carries the just-added product ids so the parent can check them
   *  against active rule triggers and warn once this drawer has closed. */
  submitted = output<string[]>();
  productPicked = output<DsmProductOption>();
  packageEntriesAdded = output<DsmSelectedProductEntry[]>();
  readonly isSaving = computed(() => this.#spinner.isButtonLoading('dsm-save-options'));
  readonly saveError = signal<string | null>(null);

  /** Read by name: a second panel instance lives in the Scenarios tab, and tab panels are not
   *  lazy, so an unnamed query could resolve to either one. */
  readonly optionsTabPanelRef = viewChild<DsmAddOptionOptionsPanelComponent>('optionsTabPanel');
  /** Kept on the modal because parent screens read it off a viewChild for their filter badge. */
  readonly activeFilterCount = computed(() => this.optionsTabPanelRef()?.activeFilterCount() ?? 0);

  readonly configFormRef = viewChild(DsmSelectedProductConfigFormComponent);
  readonly multiLocationFormRef = viewChild(DsmSelectedProductMultiLocationFormComponent);
  readonly kitFormRef = viewChild(DsmKitOptionConfigFormComponent);
  readonly configLocationCount = computed(() => this.multiLocationFormRef()?.locationCount() ?? 0);
  readonly configTotalValue = computed(() => this.multiLocationFormRef()?.totalValue() ?? 0);
  readonly editTotalValue = computed(() => this.configFormRef()?.totalPrice() ?? 0);
  readonly isActiveKit = computed(() => this.activeProduct()?.isKit ?? false);
  readonly kitTotalValue = computed(() => this.kitFormRef()?.totalPrice() ?? 0);
  readonly kitConfigInvalid = computed(() => this.kitFormRef()?.invalid() ?? true);
  readonly configBlockedByMissingAttributes = computed(() => {
    const product = this.activeProduct();
    if (!product?.selectionRequired) return false;
    return (this.multiLocationFormRef()?.productAttributes()?.length ?? 0) === 0;
  });

  readonly selectedEntries = signal<DsmSelectedProductEntry[]>([]);
  readonly panelSelectedEntries = computed(() =>
    this.isBasketSource() ? this.basketEntries() : this.selectedEntries()
  );
  readonly selectedCount = computed(() => this.selectedEntries().length);
  readonly totalSelectedOriginalPrice = computed(() =>
    this.selectedEntries().reduce(
      (total, entry) => total + (entry.values.unitPrice ?? 0) * (entry.values.quantity ?? 0),
      0
    )
  );
  readonly totalSelectedProductsPrice = computed(() =>
    this.selectedEntries().reduce((total, entry) => {
      const amount = (entry.values.unitPrice ?? 0) * (entry.values.quantity ?? 0);
      return total + this.packageService.amountAfterDiscount(amount, entry.discountPercentage);
    }, 0)
  );
  readonly hasSelectedDiscount = computed(() => this.totalSelectedOriginalPrice() > this.totalSelectedProductsPrice());

  readonly pendingProduct = signal<DsmProductOption | null>(null);
  readonly editingEntryId = signal<string | null>(null);
  readonly activeProduct = computed<DsmProductOption | null>(() => {
    const pending = this.pendingProduct();
    if (pending) return pending;
    const id = this.editingEntryId();
    if (!id) return null;
    return this.selectedEntries().find(entry => entry.id === id)?.product ?? null;
  });
  readonly initialValues = computed<DsmSelectedProductValues | null>(() => {
    const id = this.editingEntryId();
    if (!id) return null;
    return this.selectedEntries().find(entry => entry.id === id)?.values ?? null;
  });
  readonly productConfigDialogVisible = computed(() => this.activeProduct() !== null);
  readonly isNewConfigEntry = computed(() => this.pendingProduct() !== null);
  readonly configDialogHeader = computed(() => {
    const product = this.activeProduct();
    return `Add ${product?.description || product?.code || ''}`;
  });

  readonly selectedPanelVisible = signal(false);
  readonly expandedAddedProductIds = signal<Set<string>>(new Set());
  readonly selectedPanelPinned = linkedSignal(() => this.#isDesktop());
  readonly panelLeaving = signal(false);
  readonly activeTab = signal<'options' | 'packages' | 'scenarios'>('options');
  /** Hidden entirely when Options is the only tab available. */
  readonly tabOptions = computed(() => {
    const options: { label: string; value: 'options' | 'packages' | 'scenarios' }[] = [
      { label: 'Options', value: 'options' }
    ];
    if (this.showPackagesTab()) options.push({ label: 'Packages', value: 'packages' });
    if (this.showScenariosTab()) options.push({ label: 'Scenarios', value: 'scenarios' });
    return options;
  });
  #leaveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {}

  onTabChange(tab: 'options' | 'packages' | 'scenarios' | null): void {
    if (!tab) return;
    this.activeTab.set(tab);
  }

  #entryIdCounter = 0;

  toggleExpandedAddedProduct(id: string): void {
    this.expandedAddedProductIds.update(set => {
      const next = new Set(set);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  close(): void {
    this.dialogClose.emit();
  }

  openSelectedPanel(): void {
    this.selectedPanelVisible.set(true);
  }

  closeSelectedPanel(): void {
    this.selectedPanelVisible.set(false);
  }

  togglePin(): void {
    const wasPinned = this.selectedPanelPinned();
    if (wasPinned) {
      this.selectedPanelPinned.set(false);
      this.selectedPanelVisible.set(true);
    } else {
      this.selectedPanelPinned.set(true);
      this.selectedPanelVisible.set(false);
    }
  }

  addProduct(product: DsmProductOption): void {
    if (this.isBasketSource()) {
      this.productPicked.emit(product);
      return;
    }
    if (this.isPackageSource()) {
      this.selectedEntries.update(entries => [
        {
          id: this.#generateEntryId(),
          product,
          values: {
            locationId: null,
            quantity: product.quantity || 1,
            unitPrice: product.unitPrice ?? 0,
            notes: '',
            attributeValues: {}
          }
        },
        ...entries
      ]);
      return;
    }
    this.pendingProduct.set(product);
    this.editingEntryId.set(null);
  }

  openEditConfig(entryId: string): void {
    this.pendingProduct.set(null);
    this.editingEntryId.set(entryId);
  }

  onProductConfigDialogVisibilityChange(visible: boolean): void {
    if (!visible) this.cancelProductConfig();
  }

  cancelProductConfig(): void {
    this.pendingProduct.set(null);
    this.editingEntryId.set(null);
  }

  confirmProductConfig(): void {
    if (this.isActiveKit()) {
      this.kitFormRef()?.attemptCommit();
    } else if (this.isNewConfigEntry()) {
      this.multiLocationFormRef()?.attemptCommit();
    } else {
      this.configFormRef()?.attemptCommit();
    }
  }

  onKitCommit(values: DsmSelectedProductValues): void {
    if (this.isNewConfigEntry()) {
      this.onConfigCommit([values]);
    } else {
      this.onEditCommit(values);
    }
  }

  onConfigCommit(valuesArray: DsmSelectedProductValues[]): void {
    if (!valuesArray.length) return;
    const product = this.pendingProduct();
    if (!product) return;
    const newEntries = valuesArray.map(values => ({ id: this.#generateEntryId(), product, values }));
    this.selectedEntries.update(entries => [...newEntries, ...entries]);
    this.cancelProductConfig();
  }

  onEditCommit(values: DsmSelectedProductValues): void {
    const id = this.editingEntryId();
    if (!id) return;
    this.selectedEntries.update(entries => entries.map(entry => (entry.id === id ? { ...entry, values } : entry)));
    this.cancelProductConfig();
  }

  updateQuantity(entryId: string, quantity: number): void {
    this.selectedEntries.update(entries =>
      entries.map(entry => (entry.id === entryId ? { ...entry, values: { ...entry.values, quantity } } : entry))
    );
  }

  onLinkedProductUpdated(
    event: { modelElevationProductsId: string; values: DsmSelectedProductValues },
    entryId: string
  ): void {
    this.selectedEntries.update(entries =>
      entries.map(entry => {
        if (entry.id !== entryId) return entry;
        const linkedProducts = (entry.values.linkedProducts ?? []).map(linked =>
          linked.modelElevationProductsId === event.modelElevationProductsId ? { ...linked, ...event.values } : linked
        );
        return { ...entry, values: { ...entry.values, linkedProducts } };
      })
    );
  }

  removeProduct(entryId: string): void {
    this.selectedEntries.update(current => current.filter(entry => entry.id !== entryId));

    if (this.selectedEntries().length === 0 && (this.selectedPanelPinned() || this.selectedPanelVisible())) {
      this.panelLeaving.set(true);
      this.#leaveTimer = setTimeout(() => {
        this.selectedPanelPinned.set(false);
        this.selectedPanelVisible.set(false);
        this.panelLeaving.set(false);
        this.#leaveTimer = null;
      }, 300);
    }
  }

  onPackageEntriesAdded(entries: DsmSelectedProductEntry[]): void {
    if (!entries.length) return;
    if (this.isBasketSource()) {
      this.packageEntriesAdded.emit(entries);
      return;
    }
    this.selectedEntries.update(current => [...entries, ...current]);
  }

  save(): void {
    if (!this.selectedEntries().length) return;

    this.saveError.set(null);
    const request$ = this.isPackageSource()
      ? this.packageService.addPackageProducts(this.addendumId(), {
          modelElevationProductsIds: [
            ...new Set(this.selectedEntries().map(entry => entry.product.modelElevationProductsId))
          ]
        })
      : this.addendumService.addProducts(this.addendumId(), this.selectedEntries().map(toDsmAddendumProductPayload));

    request$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: () => {
        const addedProductIds = [...new Set(this.selectedEntries().map(entry => entry.product.productId))].filter(
          (id): id is string => !!id
        );
        this.submitted.emit(addedProductIds);
        this.dialogClose.emit();
      },
      error: () => this.saveError.set('Could not save options. Please retry.')
    });
  }

  ngOnDestroy(): void {
    if (this.#leaveTimer) clearTimeout(this.#leaveTimer);
  }

  #generateEntryId(): string {
    this.#entryIdCounter += 1;
    return `selection-${Date.now()}-${this.#entryIdCounter}`;
  }
}
